import { useEffect, useReducer, useRef } from 'react';
import { useFormAugmentedContext } from '../form/augmented/FormAugmentedContext.tsx';
import {
  DEFAULT_FIELD_DERIVED_STATE,
  DERIVED_STATE_KEYS,
  type FieldDerivedStateKey,
  type IFieldDerivedState,
} from './IFieldDerivedState.ts';
import { type IResolvedSchemaStore } from './ResolvedSchemaStore.ts';

/**
 * Legge il derived state di uno o più campi dallo store centralizzato.
 *
 * **Proxy pattern** (stile RHF `getProxyFormState`): le proprietà
 * restituite sono getter che tracciano quali proprietà sono accedute.
 * Il re-render avviene SOLO quando una proprietà effettivamente
 * acceduta dal consumer cambia.
 *
 * Se il consumer non accede a nessuna proprietà derivata,
 * la subscription non triggera MAI un re-render.
 */
export function useResolvedFieldState(names: string[]) {
  const { resolvedStore } = useFormAugmentedContext();

  // Tracking delle proprietà accedute dal consumer
  const accessedProps = useRef(new Set<FieldDerivedStateKey>());

  // forceRender: il proxy legge sempre dallo store (source of truth),
  // quindi basta forzare un re-render per propagare il nuovo valore.
  const [, forceRender] = useReducer(c => c + 1, 0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: names.join è la chiave stabile
  useEffect(() => {
    return resolvedStore.subscribe(names, changedProps => {
      // Re-render solo se almeno una proprietà acceduta è cambiata
      for (const prop of changedProps) {
        if (accessedProps.current.has(prop)) {
          forceRender();
          return;
        }
      }
    });
  }, [resolvedStore, names.join(',')]);

  // Costruisci oggetto con getter che tracciano accesso
  return buildProxiedStates(names, resolvedStore, accessedProps.current);
}

/**
 * Crea un array di oggetti con `Object.defineProperty` getter.
 * Ogni accesso a una proprietà (es. `state.visible`) marca quella
 * proprietà come "tracked" nel Set `accessedProps`.
 *
 * Il getter legge sempre dallo store (source of truth): non serve
 * stato locale, basta un `forceRender()` per propagare il nuovo valore.
 */
function buildProxiedStates(
  names: string[],
  store: IResolvedSchemaStore,
  accessedProps: Set<FieldDerivedStateKey>,
): IFieldDerivedState[] {
  return names.map(name => {
    const result = {} as IFieldDerivedState;
    for (const key of DERIVED_STATE_KEYS) {
      Object.defineProperty(result, key, {
        enumerable: true,
        get() {
          accessedProps.add(key);
          return (store.getState(name) ?? DEFAULT_FIELD_DERIVED_STATE)[key];
        },
      });
    }
    return result;
  });
}
